import React from "react";
import { useTranslation } from "react-i18next";
import './InputMessage.scss';
import {
  ArrowMoveDownCustomIcon,
  CloseIcon,
  ChevronRightIcon,
  ChatArrowUpIcon,
  OtherFeaturesIcon,
  ImageCompositionIcon,
  AudioIcon,
  TaxIcon,
  ReportIcon,
  GuideLinesIcon,
  ManualArrowIcon,
  PostIcon,
  TickIcon,
  UploadAttachmentsIcon,
  UploadImageIcon,
  VideoIcon,
  ImageUploadErrorIcon,
  ImageUploadingIcon,
  PauseIcon,
  VoiceStopIcon,
  InstructionIcon,
  CancelVoiceIcon,
  PauseVoiceIcon,
  SendNewIcon,
  PlusChatbotIcon,
  VoiceChatbotIcon,
} from "@assets/icon";
import {
  getNumberSuggestions,
  getQuickFunctionSuggestions,
  getInstructionSuggestions,
  parseKeywordSuggestionInput,
  safePostMessage,
  type KeywordSuggestionMode,
} from "@utils/index";
import { ChatAgentType, isAgentFeatureTag } from "@constants/chat-agents";
import type { ChatMessage } from "../../../../types/chat-agent";
import QuotedMessagePreview from "./QuotedMessagePreview";
import MoneySuggestions from "./MoneySuggestions";
import ComponentPicker, { ComponentTypeIcon } from "./ComponentPicker";
import CskhPlusDrawer, { type CskhPlusActionKey } from "./CskhPlusDrawer";
import FeatureSuggestionDropdown from "./FeatureSuggestionDropdown";
import AtFeatureSuggestions from "./AtFeatureSuggestions";
import OtherFeaturesFlyout from './OtherFeaturesFlyout';
import { formatFeatureSuggestion } from './featureSuggestionUtils';
import { getOtherFeatureCommands } from './otherFeaturesUtils';
import type { DefaultCommand } from '../../../../types/chat-command';
import { getComponentChipLabel, COMPONENT_TYPE_ORDER, type ChatComponentItem } from '../../../../types/chat-component';
import { MAX_IMAGES_COUNT, resizeImage, validateFile } from "@utils/fileUpload";
import { extractDirectPdfUrl } from "@utils/command";
import { chatService, type FeatureSuggestItem } from "@services/chatService";
import { useComponentPicker, COMPONENT_TAB_META } from '../../../../hooks/useComponentPicker';
import { useEmbedSubmenuBleed } from '../../../../hooks/useEmbedSubmenuBleed';
import { useFeatureSuggestions } from '../../../../hooks/useFeatureSuggestions';
import { PostMessageType } from '@constants/postMessage';

type PendingAttachmentChange =
  | { kind: 'open-file-picker' }
  | { kind: 'open-component-picker' }
  | { kind: 'apply-component'; item: ChatComponentItem }
  | { kind: 'upload-files'; files: File[] };

export type ChatUploadedImage = {
  contentType: string;
  imageUrl: string;
  name: string;
  uploadStatus: string;
};

interface InputMessageProps {
  onSendMessage: (message: string, images?: ChatUploadedImage[], component?: ChatComponentItem | null) => void;
  setToastMessage: (toast: any) => void;
  handleMicrophoneClick: () => void;
  stopMicAndDiscard?: () => void;
  pauseMicAndKeepText?: () => void;
  cancelMicAndClearText?: () => void;
  inputValue: string;
  listening: boolean;
  onChangeInput: (value: string, cursorPosition?: number) => void;
  placeholder?: string;
  hasCommands?: boolean;
  activeAgent?: ChatAgentType;
  onInstructionClick?: () => void;
  onQuickFunctionClick?: () => void;
  quickFunctions?: any[];
  quickFunctionSearchList?: any[];
  instructions?: any[];
  quotedMessage?: ChatMessage | null;
  onCancelQuote?: () => void;
  isChatInputDisabled?: boolean;
  isBotSpeaking?: boolean;
  isThinking?: boolean;
  isMicDisabled?: boolean;
  hasVoicePermissionError?: boolean;
  onSelectQuickFunction?: (item: any) => void;
  onSelectInstruction?: (item: any) => void;
  isQuickFunctionsLoading?: boolean;
  isInstructionsLoading?: boolean;
  commands?: DefaultCommand[];
  onSelectCommand?: (item: DefaultCommand) => void;
  isCommandsLoading?: boolean;
  onStopSpeaking?: () => void;
  isBotStreaming?: boolean;
  onStopStreaming?: () => void;
  permissions?: Record<string, boolean> | null;
  isOwner?: boolean;
  /** Disable + / component picker (order countdown or confirm switch flow) */
  isComponentPickerDisabled?: boolean;
  selectedPlusFeatureKey?: string | null;
  onSelectedPlusFeatureChange?: (key: string | null) => void;
}

const KeywordSuggestionLine: React.FC<{ mode: KeywordSuggestionMode; text: string; inputValue: string }> = ({ mode, text, inputValue }) => {
  const fullPrefix = mode === 'INSTRUCTION' ? 'Hướng dẫn' : 'Mở';
  const leadRe = mode === 'INSTRUCTION' ? /^\s*Hướng dẫn\s*/i : /^\s*Mở\s*/i;
  const rawBody = text.replace(leadRe, '').trim();
  const fullText = rawBody ? `${fullPrefix} ${rawBody}` : fullPrefix;

  const trimmedInput = inputValue.trim();
  const lowFull = fullText.toLowerCase();
  const lowInput = trimmedInput.toLowerCase();

  // Direct prefix match (e.g. "hướng dẫn sử" → "Hướng dẫn sử dụng")
  if (lowInput.length > 0 && lowFull.startsWith(lowInput)) {
    const boldPart = fullText.substring(0, trimmedInput.length);
    const restPart = fullText.substring(trimmedInput.length);
    return (
      <span className="input-message-keyword-suggestion-label">
        <strong>{boldPart}</strong>{restPart}
      </span>
    );
  }

  // "?" / ">" only open the list — do not bold the first letter of "Hướng dẫn" / "Mở"
  if (/^(\?|>)$/.test(trimmedInput)) {
    return (
      <span className="input-message-keyword-suggestion-label">
        {fullText}
      </span>
    );
  }

  // Shorthand (huo, mo, ...) → bold the same number of characters as typed
  const boldLen = Math.min(trimmedInput.length, fullText.length);
  const boldPart = fullText.substring(0, boldLen);
  const restPart = fullText.substring(boldLen);
  return (
    <span className="input-message-keyword-suggestion-label">
      <strong>{boldPart}</strong>{restPart}
    </span>
  );
};

interface PlusButtonProps {
  buttonRef: React.RefObject<HTMLButtonElement | null>;
  isOpen: boolean;
  disabled: boolean;
  isCountdownDisabled: boolean;
  onClick: () => void;
}

const PlusButton: React.FC<PlusButtonProps> = ({
  buttonRef,
  isOpen,
  disabled,
  isCountdownDisabled,
  onClick,
}) => {
  
  
  const tooltipText = isOpen ? 'Đóng' : 'Thêm';
  const className = `input-message-action-btn input-message-plus-btn ${isOpen ? 'is-open' : ''} ${disabled ? 'disabled' : ''} ${isCountdownDisabled ? 'is-countdown-disabled' : ''}`;
  return (
    <button
      ref={buttonRef as any}
      className={className}
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-tooltip-id="chatbot-tooltip"
      data-tooltip-content={tooltipText}
      data-tooltip-place="top"
      aria-label={tooltipText}
    >
      <PlusChatbotIcon aria-hidden="true" />
    </button>
  );
};

interface MicButtonProps {
  disabled: boolean;
  onClick: () => void;
}

const MicButton: React.FC<MicButtonProps> = ({ disabled, onClick }) => (
  <button
    className={`input-message-action-btn mic-btn shrink-0 ${disabled ? 'disabled' : ''}`}
    onClick={onClick}
    onMouseUp={(e) => (e.target as HTMLButtonElement)?.blur?.()}
    type="button"
    disabled={disabled}
    data-tooltip-id="chatbot-tooltip"
    data-tooltip-content="Nhập bằng giọng nói"
    data-tooltip-place="top"
    aria-label="Nhập bằng giọng nói"
  >
    <VoiceChatbotIcon aria-hidden="true" />
  </button>
);

const getTextareaClassName = (isVoiceMode: boolean): string => {
  const baseClass =
    "input-message-textarea !mb-0 !min-h-[40px] !py-2 !font-['Roboto',sans-serif] !text-[16px] !font-normal !text-[#242429] !leading-6 placeholder:!font-['Roboto',sans-serif] placeholder:!text-[16px] placeholder:!font-normal placeholder:!text-[#94949A]";
  if (isVoiceMode) {
    return `${baseClass} is-voice`;
  }
  return baseClass;
};

const InputMessage: React.FC<InputMessageProps> = ({
  onSendMessage,
  setToastMessage,
  handleMicrophoneClick,
  stopMicAndDiscard,
  pauseMicAndKeepText,
  cancelMicAndClearText,
  inputValue,
  listening,
  onChangeInput,
  placeholder,
  hasCommands,
  activeAgent,
  onInstructionClick,
  onQuickFunctionClick,
  quickFunctions = [],
  quickFunctionSearchList = [],
  instructions = [],
  quotedMessage,
  onCancelQuote,
  isChatInputDisabled = false,
  isBotSpeaking = false,
  isThinking = false,
  isMicDisabled = false,
  hasVoicePermissionError = false,
  onSelectQuickFunction,
  onSelectInstruction,
  isQuickFunctionsLoading = false,
  isInstructionsLoading = false,
  commands = [],
  onSelectCommand,
  isCommandsLoading = false,
  onStopSpeaking,
  isBotStreaming = false,
  onStopStreaming,
  permissions,
  isOwner = false,
  isComponentPickerDisabled = false,
  selectedPlusFeatureKey: selectedPlusFeatureKeyProp,
  onSelectedPlusFeatureChange,
}) => {
  const { t } = useTranslation();
  const [isFocused, setIsFocused] = React.useState(false);
  const [hideSuggestions, setHideSuggestions] = React.useState(false);
  const [isComponentMenuOpen, setIsComponentMenuOpen] = React.useState(false);
  const [isOtherFeaturesHovered, setIsOtherFeaturesHovered] = React.useState(false);
  const [isOtherFeaturesPinned, setIsOtherFeaturesPinned] = React.useState(false);
  const [isCskhPlusDrawerOpen, setIsCskhPlusDrawerOpen] = React.useState(false);
  const [showComponentPickerView, setShowComponentPickerView] = React.useState(false);
  const [showFeatureSuggestDropdown, setShowFeatureSuggestDropdown] = React.useState(false);
  const [selectedAtSuggestionText, setSelectedAtSuggestionText] = React.useState<string | null>(null);
  const [reportCommands, setReportCommands] = React.useState<any[]>([]);
  const [isReportCommandsLoading, setIsReportCommandsLoading] = React.useState(false);
  const [selectedPlusFeatureKeyInternal, setSelectedPlusFeatureKeyInternal] = React.useState<string | null>(null);
  const selectedPlusFeatureKey = selectedPlusFeatureKeyProp !== undefined
    ? selectedPlusFeatureKeyProp
    : selectedPlusFeatureKeyInternal;
  const atFeatureSuggestions = useFeatureSuggestions({
    enabled: activeAgent === ChatAgentType.AI,
    inputValue,
  });
  const setSelectedPlusFeatureKey = (key: string | null) => {
    onSelectedPlusFeatureChange?.(key);
    if (selectedPlusFeatureKeyProp === undefined) {
      setSelectedPlusFeatureKeyInternal(key);
    }
  };
  // Tag gắn agent (Thuế/Báo cáo/Hướng dẫn) giữ nguyên sau khi gửi/chọn gợi ý/đính kèm -
  // chỉ gỡ khi user bấm X hoặc chọn tag khác (gỡ tag = đổi agent về Hỏi đáp).
  // Đọc qua ref vì hàm này được gọi trong useCallback (runPendingAttachmentChange) - tránh closure cũ.
  const selectedPlusFeatureKeyRef = React.useRef(selectedPlusFeatureKey);
  selectedPlusFeatureKeyRef.current = selectedPlusFeatureKey;
  const clearTransientFeatureTag = () => {
    const key = selectedPlusFeatureKeyRef.current;
    if (key && !isAgentFeatureTag(key)) {
      setSelectedPlusFeatureKey(null);
    }
  };
  const [componentDetailOpen, setComponentDetailOpen] = React.useState(false);
  const [attachedComponent, setAttachedComponent] = React.useState<ChatComponentItem | null>(null);
  const componentMenuRef = React.useRef<HTMLDivElement>(null);
  const componentPlusBtnRef = React.useRef<HTMLButtonElement>(null);
  const otherFeaturesTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  // Flyout "Thêm thành phần": hover để mở (giống "Tính năng khác"), click để ghim (touch)
  const componentFlyoutTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isComponentFlyoutPinned, setIsComponentFlyoutPinned] = React.useState(false);
  type ImageEntry = {
    id: string;
    previewUrl: string;
    status: 'uploading' | 'uploaded' | 'error';
    file?: File;
    uploadedData?: ChatUploadedImage;
  };

  const [imageEntries, setImageEntries] = React.useState<ImageEntry[]>([]);
  const [isVoiceActive, setIsVoiceActive] = React.useState(false);
  const prevListeningRef = React.useRef(listening);

  React.useEffect(() => {
    if (listening) {
      setIsVoiceActive(true);
    } else if (
      prevListeningRef.current ||
      (!inputValue?.trim() && imageEntries.length === 0 && !attachedComponent)
    ) {
      setIsVoiceActive(false);
    }
    prevListeningRef.current = listening;
  }, [listening, inputValue, imageEntries.length, attachedComponent]);

  React.useEffect(() => {
    if (hasVoicePermissionError) {
      setIsVoiceActive(false);
    }
  }, [hasVoicePermissionError]);

  const uploadLoading = imageEntries.some(e => e.status === 'uploading');
  const pendingAttachmentChangeRef = React.useRef<PendingAttachmentChange | null>(null);
  const processUploadFilesRef = React.useRef<(files: File[]) => Promise<void>>(async () => {});

  const {
    allowedTypes,
    selectedType,
    selectType,
    searchInput,
    setSearchInput,
    commitSearch,
    activeCache,
    loadMore,
  } = useComponentPicker({
    open: isComponentMenuOpen,
    permissions,
    isOwner,
  });

  const canShowComponentPicker =
    (activeAgent === ChatAgentType.AI || activeAgent === ChatAgentType.ANALYTICS_QUESTION) &&
    allowedTypes.length > 0;

  // Flyout loại thành phần mở sang trái menu "+" (giống submenu command "?") → nới iframe sang trái
  const isTypeFlyoutOpen =
    activeAgent !== ChatAgentType.CSKH &&
    isComponentMenuOpen &&
    !componentDetailOpen &&
    canShowComponentPicker &&
    showComponentPickerView;

  const displayPlaceholder = listening
    ? (t('common.listeningPlaceholder') || "Đang nghe bạn nói...")
    : activeAgent === ChatAgentType.CSKH
      ? 'Hỏi CSKH'
      : 'Hỏi Tendoo AI';
  const hasInputExtras =
    Boolean(quotedMessage) ||
    Boolean(attachedComponent) ||
    Boolean(selectedPlusFeatureKey) ||
    imageEntries.length > 0;
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const imageEntriesRef = React.useRef<ImageEntry[]>([]);
  imageEntriesRef.current = imageEntries;
  const otherFeatureCommands = React.useMemo(() => getOtherFeatureCommands(commands), [commands]);
  const isOtherFeaturesOpen = isOtherFeaturesHovered || isOtherFeaturesPinned;

  // 1 trạng thái bleed chung cho cả 2 flyout mở sang trái ("Thêm thành phần" / "Tính năng khác"):
  // tách 2 effect riêng thì khi chuyển hover giữa 2 mục, effect đóng chạy sau gửi open:false
  // → host cắt lại flyout đang mở.
  useEmbedSubmenuBleed(isTypeFlyoutOpen || isOtherFeaturesOpen);

  React.useEffect(() => {
    return () => {
      imageEntriesRef.current.forEach((entry) => URL.revokeObjectURL(entry.previewUrl));
    };
  }, []);

  const clearImages = React.useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setImageEntries((prev) => {
      prev?.forEach((entry) => {
        if (entry?.previewUrl) {
          URL.revokeObjectURL(entry.previewUrl);
        }
      });
      return [];
    });
  }, []);

  const clearOtherFeaturesTimer = React.useCallback(() => {
    if (!otherFeaturesTimerRef.current) return;
    clearTimeout(otherFeaturesTimerRef.current);
    otherFeaturesTimerRef.current = null;
  }, []);

  const closeOtherFeatures = React.useCallback(() => {
    clearOtherFeaturesTimer();
    setIsOtherFeaturesHovered(false);
    setIsOtherFeaturesPinned(false);
  }, [clearOtherFeaturesTimer]);

  const clearComponentFlyoutTimer = React.useCallback(() => {
    if (!componentFlyoutTimerRef.current) return;
    clearTimeout(componentFlyoutTimerRef.current);
    componentFlyoutTimerRef.current = null;
  }, []);

  const closeComponentMenu = React.useCallback(() => {
    closeOtherFeatures();
    clearComponentFlyoutTimer();
    setIsComponentFlyoutPinned(false);
    setIsComponentMenuOpen(false);
    setShowComponentPickerView(false);
    setComponentDetailOpen(false);
    setSearchInput('');
  }, [closeOtherFeatures, clearComponentFlyoutTimer, setSearchInput]);

  React.useEffect(() => () => clearOtherFeaturesTimer(), [clearOtherFeaturesTimer]);
  React.useEffect(() => () => clearComponentFlyoutTimer(), [clearComponentFlyoutTimer]);

  const closeCskhPlusDrawer = React.useCallback(() => {
    setIsCskhPlusDrawerOpen(false);
  }, []);

  const prevActiveAgentRef = React.useRef<ChatAgentType | undefined>(activeAgent);

  React.useEffect(() => {
    if (
      prevActiveAgentRef.current === ChatAgentType.AI &&
      activeAgent === ChatAgentType.CSKH
    ) {
      clearImages();
    }
    prevActiveAgentRef.current = activeAgent;
  }, [activeAgent, clearImages]);

  React.useEffect(() => {
    if (activeAgent !== ChatAgentType.CSKH) {
      setIsCskhPlusDrawerOpen(false);
    }
  }, [activeAgent]);

  // Mở dropdown gợi ý khi chọn tag Hướng dẫn / Báo cáo / Thuế
  React.useEffect(() => {
    if (
      selectedPlusFeatureKey === 'manual' ||
      selectedPlusFeatureKey === 'report' ||
      selectedPlusFeatureKey === 'tax'
    ) {
      setShowFeatureSuggestDropdown(true);
    } else {
      setShowFeatureSuggestDropdown(false);
    }
  }, [selectedPlusFeatureKey]);

  // Tag Báo cáo → command default ANALYTICS_QUESTION; tag Thuế → command default TAX_QUESTION (tài liệu PDF)
  React.useEffect(() => {
    const commandAgent =
      selectedPlusFeatureKey === 'report'
        ? ChatAgentType.ANALYTICS_QUESTION
        : selectedPlusFeatureKey === 'tax'
          ? ChatAgentType.TAX_QUESTION
          : null;
    if (!commandAgent) {
      setReportCommands([]);
      setIsReportCommandsLoading(false);
      return;
    }

    let cancelled = false;
    const fetchReportCommands = async () => {
      const token = sessionStorage.getItem('access_token');
      if (!token) return;

      setIsReportCommandsLoading(true);
      try {
        const res = await chatService.getDefaultCommands(token, commandAgent);
        const commandData = res?.data?.data || res?.data || res;
        if (cancelled) return;
        if (Array.isArray(commandData)) {
          setReportCommands(
            commandData.map((cmd: any) => ({
              ...cmd,
              icon: cmd.imageUrl || cmd.icon,
              iconBackground: cmd.imageUrl || cmd.iconBackground,
            })),
          );
        } else {
          setReportCommands([]);
        }
      } catch (error) {
        console.error(`Failed to fetch ${commandAgent} commands:`, error);
        if (!cancelled) setReportCommands([]);
      } finally {
        if (!cancelled) setIsReportCommandsLoading(false);
      }
    };

    void fetchReportCommands();
    return () => {
      cancelled = true;
    };
  }, [selectedPlusFeatureKey]);

  const closeFeatureSuggestDropdown = React.useCallback(() => {
    setShowFeatureSuggestDropdown(false);
  }, []);

  const featureSuggestDropdownProps = React.useMemo(() => {
    if (selectedPlusFeatureKey === 'manual') {
      return {
        items: instructions,
        isLoading: isInstructionsLoading,
        ariaLabel: 'Hướng dẫn sử dụng',
        defaultIcon: <InstructionIcon />,
        trailingIcon: <ManualArrowIcon />,
        onSelect: (item: any) => {
          onSelectInstruction?.(item);
          setShowFeatureSuggestDropdown(false);
        },
      };
    }
    if (selectedPlusFeatureKey === 'report') {
      return {
        items: reportCommands,
        isLoading: isReportCommandsLoading,
        ariaLabel: 'Gợi ý báo cáo',
        defaultIcon: <ReportIcon />,
        trailingIcon: <ChevronRightIcon />,
        onSelect: (item: any) => {
          onSelectCommand?.(item);
          setShowFeatureSuggestDropdown(false);
        },
      };
    }
    if (selectedPlusFeatureKey === 'tax') {
      return {
        items: reportCommands,
        isLoading: isReportCommandsLoading,
        ariaLabel: 'Tài liệu thuế',
        defaultIcon: <TaxIcon />,
        trailingIcon: <ChevronRightIcon />,
        onSelect: (item: any) => {
          // Command thuế không nằm trong danh sách command của agent hiện tại → mở PDF tại chỗ
          const pdfUrl = extractDirectPdfUrl(item);
          if (pdfUrl) {
            window.open(pdfUrl, '_blank');
          } else {
            onSelectCommand?.(item);
          }
          setShowFeatureSuggestDropdown(false);
        },
      };
    }
    return null;
  }, [
    selectedPlusFeatureKey,
    instructions,
    isInstructionsLoading,
    reportCommands,
    isReportCommandsLoading,
    onSelectInstruction,
    onSelectCommand,
  ]);

  const runPendingAttachmentChange = React.useCallback((pending: PendingAttachmentChange) => {
    if (pending.kind === 'open-file-picker') {
      setAttachedComponent(null);
      fileInputRef.current?.click();
      return;
    }
    if (pending.kind === 'open-component-picker') {
      clearImages();
      setComponentDetailOpen(false);
      setIsComponentFlyoutPinned(true);
      setShowComponentPickerView(true);
      setIsComponentMenuOpen(true);
      return;
    }
    if (pending.kind === 'apply-component') {
      setAttachedComponent(pending.item);
      clearTransientFeatureTag();
      clearImages();
      closeComponentMenu();
      return;
    }
    if (pending.kind === 'upload-files') {
      setAttachedComponent(null);
      clearTransientFeatureTag();
      void processUploadFilesRef.current(pending.files);
    }
  }, [clearImages, closeComponentMenu]);

  const requestChangeAttachment = (pending: PendingAttachmentChange) => {
    pendingAttachmentChangeRef.current = pending;
    const isImageComponentSwap =
      pending.kind === 'open-file-picker' ||
      pending.kind === 'open-component-picker' ||
      pending.kind === 'upload-files' ||
      (pending.kind === 'apply-component' && imageEntries.length > 0);
    const from = isImageComponentSwap ? 'image' : 'component';
    safePostMessage({
      type: PostMessageType.CHANGE_ATTACHMENT,
      payload: { from },
    });
  };

  React.useEffect(() => {
    const handler = (event: MessageEvent) => {
      const { type, payload } = event.data || {};
      if (type !== PostMessageType.CHANGE_ATTACHMENT_RESULT) return;

      const pending = pendingAttachmentChangeRef.current;
      pendingAttachmentChangeRef.current = null;
      if (!payload?.confirmed || !pending) return;
      runPendingAttachmentChange(pending);
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [runPendingAttachmentChange]);

  const handleAttachClick = () => {
    if (attachedComponent) {
      requestChangeAttachment({ kind: 'open-file-picker' });
      return;
    }
    fileInputRef.current?.click();
  };

  const applyAttachedComponent = (item: ChatComponentItem) => {
    const pending: PendingAttachmentChange = { kind: 'apply-component', item };
    const needsReplace =
      imageEntries.length > 0 ||
      Boolean(attachedComponent && attachedComponent.id !== item.id);

    if (needsReplace) {
      requestChangeAttachment(pending);
      return;
    }

    runPendingAttachmentChange(pending);
  };

  React.useEffect(() => {
    if (!isComponentMenuOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (componentMenuRef.current?.contains(target)) return;
      // Nút +/- tự toggle; không đóng ở đây kẻo onClick mở lại
      if (componentPlusBtnRef.current?.contains(target)) return;
      closeComponentMenu();
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isComponentMenuOpen, closeComponentMenu]);

  React.useEffect(() => {
    if (!isComponentMenuOpen) return;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeComponentMenu();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isComponentMenuOpen, closeComponentMenu]);

  React.useEffect(() => {
    if (isComponentPickerDisabled) {
      closeComponentMenu();
    }
  }, [isComponentPickerDisabled, closeComponentMenu]);

  const processUploadFiles = async (files: File[]) => {
    const validFiles: File[] = [];
    for (const file of files) {
      const error = validateFile(file);
      if (error) {
        setToastMessage({ text: error, type: 'error' });
        return;
      }
      validFiles.push(file);
    }

    const currentCount = imageEntriesRef.current.length;
    const remainingSlots = MAX_IMAGES_COUNT - currentCount;
    if (remainingSlots <= 0) {
      setToastMessage({ text: `Bạn đã chọn đủ tối đa ${MAX_IMAGES_COUNT} ảnh`, type: 'error' });
      return;
    }

    const toAdd = validFiles.slice(0, remainingSlots);
    if (validFiles.length > remainingSlots) {
      setToastMessage({ text: `Bạn đã chọn đủ tối đa ${MAX_IMAGES_COUNT} ảnh`, type: 'error' });
    }

    const newEntries: ImageEntry[] = toAdd.map((file) => ({
      id: `${Date.now()}-${Math.random()}`,
      previewUrl: URL.createObjectURL(file),
      status: 'uploading' as const,
      file,
    }));
    const entryIds = newEntries.map(e => e.id);

    setImageEntries(prev => [...prev, ...newEntries]);

    try {
      const token = sessionStorage.getItem('access_token');
      const conversationId = sessionStorage.getItem('current_conversation_id');
      if (!token || !conversationId) {
        setImageEntries(prev => prev.filter(e => !entryIds.includes(e.id)));
        newEntries.forEach(e => URL.revokeObjectURL(e.previewUrl));
        return;
      }

      const resizedFiles: File[] = [];
      for (const file of toAdd) {
        const resizedFile = await resizeImage(file);
        resizedFiles.push(resizedFile);
      }

      const uploadResponse = await chatService.uploadImages(
        token,
        parseInt(conversationId, 10),
        resizedFiles
      );

      const imgs = uploadResponse?.data;
      if (!imgs || imgs.length === 0) {
        setImageEntries(prev => prev.map(e =>
          entryIds.includes(e.id) ? { ...e, status: 'error' as const } : e
        ));
        return;
      }

      setImageEntries(prev => prev.map(e => {
        const idx = entryIds.indexOf(e.id);
        if (idx !== -1 && imgs[idx]) {
          return { ...e, status: 'uploaded' as const, uploadedData: imgs[idx] };
        }
        return e;
      }));
    } catch (error) {
      console.error('Error uploading images:', error);
      setImageEntries(prev => prev.map(e =>
        entryIds.includes(e.id) ? { ...e, status: 'error' as const } : e
      ));
    }
  };
  processUploadFilesRef.current = processUploadFiles;

  const handleUploadFiles = async (files: File[]) => {
    if (attachedComponent) {
      requestChangeAttachment({ kind: 'upload-files', files });
      return;
    }
    await processUploadFiles(files);
  };

  const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const files = Array.from(e.target.files);
    await handleUploadFiles(files);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handlePaste = async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (!e.clipboardData) return;

    const files = Array.from(e.clipboardData.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));

    if (imageFiles.length > 0) {
      e.preventDefault();

      if (isThinking || uploadLoading || isChatInputDisabled) {
        return;
      }

      await handleUploadFiles(imageFiles);
    }
  };

  const handleRemoveImage = (index: number) => {
    setImageEntries((prev) => {
      const entry = prev[index];
      if (entry) URL.revokeObjectURL(entry.previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleRetryUploadImage = async (index: number) => {
    const entry = imageEntriesRef.current[index];
    if (!entry) return;
    if (entry.status !== 'error') return;
    if (!entry.file) return;

    const token = sessionStorage.getItem('access_token');
    const conversationId = sessionStorage.getItem('current_conversation_id');
    if (!token || !conversationId) return;

    // Mark this single thumbnail as uploading to block double clicks.
    setImageEntries((prev) =>
      prev.map((e, i) =>
        i === index
          ? {
              ...e,
              status: 'uploading' as const,
            }
          : e,
      ),
    );

    try {
      const resizedFile = await resizeImage(entry.file);
      const uploadResponse = await chatService.uploadImages(
        token,
        parseInt(conversationId, 10),
        [resizedFile],
      );

      const imgs = uploadResponse?.data;
      if (!imgs || imgs.length === 0) {
        setImageEntries((prev) =>
          prev.map((e, i) =>
            i === index
              ? {
                  ...e,
                  status: 'error' as const,
                }
              : e,
          ),
        );
        return;
      }

      setImageEntries((prev) =>
        prev.map((e, i) =>
          i === index
            ? {
                ...e,
                status: 'uploaded' as const,
                uploadedData: imgs[0],
              }
            : e,
        ),
      );
    } catch (e) {
      console.error('Retry upload image failed:', e);
      setImageEntries((prev) =>
        prev.map((en, i) =>
          i === index
            ? {
                ...en,
                status: 'error' as const,
              }
            : en,
        ),
      );
    }
  };

  const handleInputChange = (value: string) => {
    setHideSuggestions(false);
    if (value !== selectedAtSuggestionText) setSelectedAtSuggestionText(null);

    // Nhập text → tắt dropdown gợi ý (Hướng dẫn / Báo cáo)
    if (showFeatureSuggestDropdown) {
      setShowFeatureSuggestDropdown(false);
    }

    // Stop speaking if typing
    if (isBotSpeaking && onStopSpeaking) {
      onStopSpeaking();
    }

    onChangeInput(value);
  };

  const openPlusMenu = () => {
    if (activeAgent === ChatAgentType.CSKH) {
      closeComponentMenu();
      setIsCskhPlusDrawerOpen(true);
      return;
    }
    setShowComponentPickerView(false);
    setComponentDetailOpen(false);
    setIsComponentMenuOpen(true);
  };

  const handleCskhPlusAction = (key: CskhPlusActionKey) => {
    closeCskhPlusDrawer();
    if (key === 'image') {
      handleAttachClick();
      return;
    }

    const prompts: Record<Exclude<CskhPlusActionKey, 'image'>, string> = {
      'report-bug': 'Báo lỗi',
      'suggest-improve': 'Gợi ý cải thiện sp',
      'consult-feature': 'Tư vấn tính năng mới',
    };
    onChangeInput(prompts[key]);
  };

  const openComponentPickerFromMenu = () => {
    closeOtherFeatures();
    if (isComponentPickerDisabled) {
      closeComponentMenu();
      setToastMessage({
        text: 'Không thể gửi thành phần khi đang hoàn thành đơn',
        type: 'error',
      });
      return;
    }
    if (!canShowComponentPicker) return;
    clearComponentFlyoutTimer();
    // Đã mở (do hover) → click chỉ ghim lại; click lần nữa khi đã ghim → đóng
    if (showComponentPickerView && !componentDetailOpen) {
      if (isComponentFlyoutPinned) {
        setIsComponentFlyoutPinned(false);
        setShowComponentPickerView(false);
      } else {
        setIsComponentFlyoutPinned(true);
      }
      return;
    }
    if (imageEntries.length > 0) {
      requestChangeAttachment({ kind: 'open-component-picker' });
      return;
    }
    setComponentDetailOpen(false);
    setIsComponentFlyoutPinned(true);
    setShowComponentPickerView(true);
    setIsComponentMenuOpen(true);
  };

  // Hover "Thêm thành phần" → mở danh sách loại thành phần, giống "Tính năng khác".
  // Không mở bằng hover khi đang bị chặn / đã có ảnh (cần click để hiện confirm đổi đính kèm).
  const openComponentFlyoutOnHover = () => {
    clearComponentFlyoutTimer();
    if (isComponentPickerDisabled || !canShowComponentPicker || imageEntries.length > 0) return;
    closeOtherFeatures();
    setComponentDetailOpen(false);
    setShowComponentPickerView(true);
  };

  const closeComponentFlyoutAfterDelay = () => {
    if (isComponentFlyoutPinned) return;
    clearComponentFlyoutTimer();
    componentFlyoutTimerRef.current = setTimeout(() => setShowComponentPickerView(false), 150);
  };

  const openOtherFeaturesOnHover = () => {
    clearOtherFeaturesTimer();
    clearComponentFlyoutTimer();
    setIsComponentFlyoutPinned(false);
    setShowComponentPickerView(false);
    setIsOtherFeaturesHovered(true);
  };

  const closeOtherFeaturesAfterDelay = () => {
    if (isOtherFeaturesPinned) return;
    clearOtherFeaturesTimer();
    otherFeaturesTimerRef.current = setTimeout(() => setIsOtherFeaturesHovered(false), 150);
  };

  const toggleOtherFeatures = () => {
    clearOtherFeaturesTimer();
    clearComponentFlyoutTimer();
    setIsComponentFlyoutPinned(false);
    setShowComponentPickerView(false);
    setIsOtherFeaturesHovered(false);
    setIsOtherFeaturesPinned((current) => !current);
  };

  const selectOtherFeature = (command: DefaultCommand) => {
    closeComponentMenu();
    onSelectCommand?.(command);
  };

  const handlePlusButtonClick = () => {
    if (isComponentPickerDisabled) {
      closeComponentMenu();
      closeCskhPlusDrawer();
      setToastMessage({
        text: 'Không thể gửi thành phần khi đang hoàn thành đơn',
        type: 'error',
      });
      return;
    }
    if (isThinking || uploadLoading || !!quotedMessage) return;
    if (isCskhPlusDrawerOpen) {
      closeCskhPlusDrawer();
      return;
    }
    if (isComponentMenuOpen) {
      closeComponentMenu();
      return;
    }
    openPlusMenu();
  };

  const renderPlusButton = () => (
    <PlusButton
      buttonRef={componentPlusBtnRef}
      isOpen={Boolean(isComponentMenuOpen || isCskhPlusDrawerOpen)}
      disabled={Boolean(isThinking || uploadLoading || !!quotedMessage || isComponentPickerDisabled)}
      isCountdownDisabled={Boolean(isComponentPickerDisabled)}
      onClick={handlePlusButtonClick}
    />
  );

  const renderVoiceCancelIcon = () => (
    <button
      className="input-message-action-btn voice-cancel-btn"
      onClick={onCancelVoice}
      type="button"
      data-tooltip-id="chatbot-voice-tooltip"
      data-tooltip-content="Đóng nhập bằng giọng nói"
      data-tooltip-place="top-start"
      aria-label="Đóng nhập bằng giọng nói"
    >
      <CancelVoiceIcon />
    </button>
  );

  const renderLeftIcon = () => {
    if (isVoiceActive) {
      return renderVoiceCancelIcon();
    }
    return renderPlusButton();
  };

  const plusMenuItems = [
    {
      key: 'upload',
      label: 'Tải ảnh lên',
      icon: <UploadImageIcon />,
      onClick: () => {
        handleAttachClick();
        closeComponentMenu();
      },
    },
    {
      key: 'component',
      label: 'Thêm thành phần',
      icon: <UploadAttachmentsIcon />,
      hasNext: true,
      onClick: openComponentPickerFromMenu,
    },
    {
      key: 'other',
      label: 'Tính năng khác',
      icon: <OtherFeaturesIcon />,
      hasAtBadge: true,
      hasNext: true,
    },
    {
      key: 'image',
      label: 'Hình ảnh',
      tooltip: 'Thiết kế ảnh theo\nchủ đề thịnh hành',
      icon: <ImageCompositionIcon />,
      separated: true,
      selectable: true,
    },
    {
      key: 'video',
      label: 'Video',
      tooltip: 'Tạo video quảng cáo\nchân thực',
      icon: <VideoIcon />,
      selectable: true,
    },
    {
      key: 'audio',
      label: 'Audio',
      tooltip: 'Tạo audio kết hợp\nnhạc và giọng nói',
      icon: <AudioIcon />,
      selectable: true,
    },
    {
      key: 'tax',
      label: 'Thuế',
      tooltip: 'Giải đáp thắc mắc thuế',
      icon: <TaxIcon />,
      selectable: true,
    },
    {
      key: 'report',
      label: 'Báo cáo',
      tooltip: 'Tổng quan tình hình\nkinh doanh',
      icon: <ReportIcon />,
      selectable: true,
    },
    {
      key: 'manual',
      label: 'Hướng dẫn',
      tooltip: 'Hướng dẫn\nsử dụng Tendoo',
      icon: <GuideLinesIcon />,
      selectable: true,
    },
    {
      key: 'post',
      label: 'Bài viết',
      tooltip: 'Viết bài bán hàng\nchốt đơn nhanh',
      icon: <PostIcon />,
      selectable: true,
    },
  ];

  const renderPlusMenu = () => {
    if (activeAgent === ChatAgentType.CSKH) return null;
    if (!isComponentMenuOpen || componentDetailOpen) return null;

    const showTypeFlyout = canShowComponentPicker && showComponentPickerView;
    const typeMenuItems = COMPONENT_TYPE_ORDER;

    return (
      <div
        ref={componentMenuRef}
        className="input-message-plus-menu absolute bottom-full left-0 z-[500] w-[196px] overflow-visible rounded-[14px] bg-white p-2 shadow-[0px_8px_24px_0px_#0000001F]"
      >
        {plusMenuItems.map((item) => (
          <React.Fragment key={item.key}>
            {item.separated && <div className="my-1 h-px bg-[#ECECF0]" />}
            <div className="relative"
              onPointerEnter={
                item.key === 'other'
                  ? openOtherFeaturesOnHover
                  : item.key === 'component'
                    ? openComponentFlyoutOnHover
                    : undefined
              }
              onPointerLeave={
                item.key === 'other'
                  ? closeOtherFeaturesAfterDelay
                  : item.key === 'component'
                    ? closeComponentFlyoutAfterDelay
                    : undefined
              }>
              <button
                type="button"
                className={`flex h-10 w-full items-center gap-2 rounded-lg border-0 p-2 text-left font-sans text-sm font-normal leading-5 text-[#242428] opacity-100 hover:bg-[#F8F8F9] ${
                  (showTypeFlyout && item.key === 'component') || (isOtherFeaturesOpen && item.key === 'other')
                    ? 'bg-[#F8F8F9]'
                    : 'bg-transparent'
                }`}
                aria-haspopup={item.key === 'other' ? 'menu' : undefined}
                aria-expanded={item.key === 'other' ? isOtherFeaturesOpen : undefined}
                aria-controls={item.key === 'other' && isOtherFeaturesOpen ? 'other-features-menu' : undefined}
                onClick={() => {
                  if (item.key === 'other') {
                    toggleOtherFeatures();
                    return;
                  }
                  if (item.selectable) {
                    setSelectedPlusFeatureKey(item.key);
                    setAttachedComponent(null);
                    closeComponentMenu();
                    return;
                  }
                  item.onClick?.();
                }}
                data-tooltip-id={item.tooltip ? 'chatbot-menu-tooltip' : undefined}
                data-tooltip-content={item.tooltip}
                data-tooltip-place={item.tooltip ? 'right' : undefined}
                data-tooltip-position-strategy={item.tooltip ? 'fixed' : undefined}
              >
                <span className="flex h-4 w-4 shrink-0 items-center justify-center text-[#3D3D43] [&>svg]:h-4 [&>svg]:w-4">
                  {item.icon}
                </span>
                <span className={`min-w-0 truncate ${item.hasAtBadge ? '' : 'flex-1'}`}>{item.label}</span>
                {item.hasAtBadge && (
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center gap-2.5 rounded-lg bg-[#F2F2F4] p-0.5 opacity-100 [&>svg]:h-[13px] [&>svg]:w-[13px] [&>svg_path]:stroke-[#62626A]">
                    <OtherFeaturesIcon />
                  </span>
                )}
                {item.hasNext && (
                  <span className="ml-auto flex h-4 w-4 shrink-0 items-center justify-center text-[#94949E] [&>svg]:h-4 [&>svg]:w-4 [&>svg_path]:stroke-[#94949E]">
                    <ChevronRightIcon />
                  </span>
                )}
                {item.selectable && selectedPlusFeatureKey === item.key && (
                  <span className="flex shrink-0 items-center justify-center text-[#242428] [&>svg]:h-[7.33px] [&>svg]:w-[9.33px]">
                    <TickIcon />
                  </span>
                )}
              </button>

              {item.key === 'component' && showTypeFlyout && (
                <div className="absolute right-full top-0 z-[501] mr-2 flex h-[276px] w-[180px] flex-col gap-1 overflow-hidden rounded-[24px] bg-white p-2 opacity-100 shadow-[0px_8px_24px_0px_#0000001F]">
                  {typeMenuItems.map((type) => (
                    <button
                      key={type}
                      type="button"
                      className="flex h-10 w-full items-center gap-2 rounded-lg border-0 bg-transparent p-2 text-left font-sans text-sm font-normal leading-5 text-[#242428] opacity-100 hover:bg-[#F8F8F9]"
                      onClick={() => {
                        selectType(type);
                        setShowComponentPickerView(false);
                        setComponentDetailOpen(true);
                      }}
                    >
                      <span className="flex h-4 w-4 shrink-0 items-center justify-center text-[#3D3D43] [&>svg]:h-4 [&>svg]:w-4">
                        <ComponentTypeIcon type={type} />
                      </span>
                      <span className="min-w-0 flex-1 truncate">
                        {COMPONENT_TAB_META[type].label}
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {item.key === 'other' && (
                <OtherFeaturesFlyout open={isOtherFeaturesOpen} commands={otherFeatureCommands}
                  loading={isCommandsLoading} onSelect={selectOtherFeature}
                  onPointerEnter={openOtherFeaturesOnHover}
                  onPointerLeave={closeOtherFeaturesAfterDelay} />
              )}
            </div>
          </React.Fragment>
        ))}
      </div>
    );
  };

  const onSend = () => {
    if (isThinking || isBotStreaming) return;

    const hasUnreadyImages = imageEntries.some(
      (e) => e.status === 'uploading' || e.status === 'error',
    );
    if (hasUnreadyImages) {
      setToastMessage({
        text: 'Ảnh chưa tải xong hoặc tải lỗi, bạn thử lại nhé',
        type: 'error',
      });
      return;
    }

    const hasText = inputValue.trim();
    const hasImages = imageEntries.some(e => e.status === 'uploaded' && e.uploadedData);
    const hasComponent = Boolean(attachedComponent);
    if (!hasText && !hasImages && !hasComponent) return;

    if (listening && stopMicAndDiscard) {
      stopMicAndDiscard();
    } else if (listening) {
      handleMicrophoneClick();
    }

    let uploadedImages: ChatUploadedImage[] | undefined;

    if (hasImages) {
      uploadedImages = imageEntries
        .filter(e => e.status === 'uploaded' && e.uploadedData)
        .map(e => e.uploadedData!);

      imageEntries.forEach(e => URL.revokeObjectURL(e.previewUrl));
      setImageEntries([]);
    }

    const componentToSend = attachedComponent;
    if (componentToSend) {
      setAttachedComponent(null);
    }
    clearTransientFeatureTag();
    setIsVoiceActive(false);

    if (hasText || (uploadedImages && uploadedImages.length > 0) || componentToSend) {
      onSendMessage(hasText ? inputValue : '', uploadedImages, componentToSend);
      if (hasText) {
        onChangeInput('');
      }
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Ignore Enter if the user is using an IME (Vietnamese Telex, Pinyin, etc.) and is currently composing a word
    if (event.nativeEvent.isComposing || event.keyCode === 229) {
      return;
    }

    if (event.key === 'Enter' && !event.shiftKey && !event.altKey) {
      event.preventDefault();
      if (!isThinking && !isBotStreaming) {
        onSend();
      }
    }
  };

  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  // Figma: text 1 dòng → cùng hàng với nút + và mic/gửi; nhiều dòng → layout 2 hàng (text ở trên, nút ở dưới)
  const [isMultilineText, setIsMultilineText] = React.useState(false);
  // Bề rộng vùng chữ khi textarea nằm cùng hàng với nút (đo lúc còn layout 1 hàng)
  const inlineTextWidthRef = React.useRef(0);
  const measureCanvasRef = React.useRef<HTMLCanvasElement | null>(null);

  const adjustTextareaHeight = React.useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    const style = window.getComputedStyle(el);
    if (!isMultilineText) {
      inlineTextWidthRef.current =
        el.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
    }
    const value = el.value;
    let multiline = value.includes('\n');
    if (!multiline && value) {
      if (!measureCanvasRef.current) {
        measureCanvasRef.current = document.createElement('canvas');
      }
      const ctx = measureCanvasRef.current.getContext('2d');
      if (ctx) {
        ctx.font = style.font;
        multiline = ctx.measureText(value).width > inlineTextWidthRef.current;
      }
    }
    if (multiline !== isMultilineText) {
      setIsMultilineText(multiline);
    }

    el.style.height = 'auto';
    const newHeight = Math.min(Math.max(40, el.scrollHeight), 120);
    el.style.height = `${newHeight}px`;
    el.style.overflowY = el.scrollHeight > 120 ? 'auto' : 'hidden';
  }, [isMultilineText]);

  React.useEffect(() => {
    adjustTextareaHeight();
  }, [inputValue, listening, isVoiceActive, adjustTextareaHeight]);

  // Remove focus when listening starts, requirement says "không cho phép focus vào khung chat để nhập text"
  React.useEffect(() => {
    if (listening && textareaRef.current) {
        textareaRef.current.blur();
    }
  }, [listening]);

  const onMicClickInternal = () => {
    if (isThinking || isMicDisabled || uploadLoading) return;
    setIsVoiceActive(true);
    closeComponentMenu();
    closeCskhPlusDrawer();
    handleMicrophoneClick();
    // After clicking, if we are now listening, ensure focus is on the input
    // We use a small timeout to ensure the state update has propagated if needed,
    // although the useEffect above should also handle it.
    setTimeout(() => {
      if (textareaRef.current && !listening) {
        textareaRef.current.focus();
      }
    }, 50);
  };

  const hasVoiceTranscript = listening && Boolean(inputValue.trim());

  const onCancelVoice = () => {
    setIsVoiceActive(false);
    if (cancelMicAndClearText) cancelMicAndClearText();
    else if (stopMicAndDiscard) stopMicAndDiscard();
    else {
      handleMicrophoneClick();
      onChangeInput('');
    }
    setTimeout(adjustTextareaHeight, 0);
  };

  const onPauseVoice = () => {
    setIsVoiceActive(false);
    if (pauseMicAndKeepText) {
      pauseMicAndKeepText();
    } else if (handleMicrophoneClick) {
      handleMicrophoneClick();
    }
    setTimeout(adjustTextareaHeight, 0);
  };

  const suggestionsData = getNumberSuggestions(inputValue);

  const showInstructionSuggestions =
    activeAgent === ChatAgentType.AI || activeAgent === ChatAgentType.INSTRUCTION_MANUAL;
  const showQuickFunctionSuggestions = activeAgent === ChatAgentType.AI;

  let { mode: keywordSuggestionMode } = parseKeywordSuggestionInput(inputValue);
  if (keywordSuggestionMode === 'QUICK_FUNCTION' && !showQuickFunctionSuggestions) {
    keywordSuggestionMode = null;
  }
  if (keywordSuggestionMode === 'INSTRUCTION' && !showInstructionSuggestions) {
    keywordSuggestionMode = null;
  }

  const quickFunctionSuggestions = showQuickFunctionSuggestions
    ? getQuickFunctionSuggestions(inputValue, quickFunctionSearchList)
    : [];
  const instructionSuggestions = showInstructionSuggestions
    ? getInstructionSuggestions(inputValue, instructions)
    : [];
  type KeywordSuggestionRow =
    | { kind: 'QUICK_FUNCTION'; item: any; displayLabel: string }
    | { kind: 'INSTRUCTION'; item: any; displayLabel: string };
  const keywordSuggestions: KeywordSuggestionRow[] =
    keywordSuggestionMode === 'QUICK_FUNCTION'
      ? quickFunctionSuggestions.map((item) => ({
          kind: 'QUICK_FUNCTION' as const,
          item,
          displayLabel: String(item.name || '').trim(),
        }))
      : keywordSuggestionMode === 'INSTRUCTION'
        ? instructionSuggestions.map((item) => ({
            kind: 'INSTRUCTION' as const,
            item,
            displayLabel:
              typeof item === 'string'
                ? item
                : String(
                    item.function_name ||
                      item.question ||
                      item.title ||
                      item.name ||
                      '',
                  ).trim(),
          }))
        : [];
  const showKeywordSuggestions =
    isFocused && !hideSuggestions && keywordSuggestions.length > 0 && keywordSuggestionMode !== null;

  const showAtFeatureSuggestions =
    isFocused &&
    activeAgent === ChatAgentType.AI &&
    atFeatureSuggestions.isTriggered &&
    selectedAtSuggestionText !== inputValue &&
    (atFeatureSuggestions.isLoading || atFeatureSuggestions.items.length > 0);

  const showNumberSuggestions =
    !showAtFeatureSuggestions && !showKeywordSuggestions && suggestionsData && activeAgent !== ChatAgentType.CSKH;

  const selectAtFeatureSuggestion = (item: FeatureSuggestItem) => {
    const selectedText = formatFeatureSuggestion(item);
    setSelectedAtSuggestionText(selectedText);
    onChangeInput(selectedText, selectedText.length);
    setTimeout(() => {
      textareaRef.current?.focus();
      textareaRef.current?.setSelectionRange(selectedText.length, selectedText.length);
    }, 0);
  };

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const cursorPosition = event.target.selectionStart;
    onChangeInput(event.target.value, cursorPosition);
  };

  return (
    <div className="input-message-container relative">
      <input
        type="file"
        ref={fileInputRef}
        onChange={onFileChange}
        multiple
        accept=".jpg,.jpeg,.png"
        style={{ display: 'none' }}
      />
      {showFeatureSuggestDropdown && featureSuggestDropdownProps && (
        <FeatureSuggestionDropdown
          open
          fiveItems
          onClose={closeFeatureSuggestDropdown}
          {...featureSuggestDropdownProps}
        />
      )}

      {!showAtFeatureSuggestions && showKeywordSuggestions && (
        <div
          className={`input-message-keyword-suggestions${
            keywordSuggestionMode === 'INSTRUCTION'
              ? ' input-message-keyword-suggestions--instruction'
              : ''
          }`}
          role="listbox"
        >
          {keywordSuggestions.map((row, index) => (
            <button
              key={`${row.kind}-${index}`}
              type="button"
              role="option"
              className="input-message-keyword-suggestion-row"
              onMouseDown={(e) => {
                e.preventDefault();
                setHideSuggestions(true);
                onChangeInput(row.displayLabel);
                setTimeout(() => {
                  if (textareaRef.current) {
                    textareaRef.current.focus();
                    const valLen = row.displayLabel.length;
                    textareaRef.current.setSelectionRange(valLen, valLen);
                  }
                }, 10);
              }}
            >
              <KeywordSuggestionLine mode={row.kind} text={row.displayLabel} inputValue={inputValue} />
              <span className="input-message-keyword-suggestion-chevron" aria-hidden>
                <ArrowMoveDownCustomIcon />
              </span>
            </button>
          ))}
        </div>
      )}

      {showNumberSuggestions && (
        <MoneySuggestions
          suggestions={suggestionsData.suggestions}
          onSelectSuggestion={(item) => {
            const before = inputValue.substring(0, suggestionsData.startIndex);
            const after = inputValue.substring(suggestionsData.endIndex);
            // Thêm khoảng trắng sau số để tránh trigger gợi ý tiếp cho số 0
            const newValue = `${before}${item.label} ${after}`;
            onChangeInput(newValue);

            // Auto focus back to input
            setTimeout(() => {
              if (textareaRef.current) {
                textareaRef.current.focus();
                // Move cursor to end
                const valLen = newValue.length;
                textareaRef.current.setSelectionRange(valLen, valLen);
              }
            }, 10);
          }}
        />
      )}

      <div className="input-message-input-anchor relative mx-auto w-[356px] max-w-full">
        {showAtFeatureSuggestions && (
          <AtFeatureSuggestions
            items={atFeatureSuggestions.items}
            isLoading={atFeatureSuggestions.isLoading}
            onSelect={selectAtFeatureSuggestion}
          />
        )}
        {renderPlusMenu()}
        {activeAgent === ChatAgentType.CSKH && (
          <CskhPlusDrawer
            open={isCskhPlusDrawerOpen}
            onClose={closeCskhPlusDrawer}
            onAction={handleCskhPlusAction}
            buttonRef={componentPlusBtnRef}
          />
        )}

      <div
        className={`input-message-card !w-full !max-w-full !p-2 ${
          listening
            ? 'is-listening !shadow-[0px_4px_20px_rgba(255,147,104,0.25)]'
            : '!shadow-[0px_4px_20px_0px_#00000017]'
        } ${
          hasInputExtras || hasVoiceTranscript || (isMultilineText && !listening)
            ? '!h-auto !min-h-[68px] !justify-start !rounded-[28px]'
            : '!h-auto !min-h-[68px] !justify-center !rounded-full'
        }`}
      >
        {canShowComponentPicker && isComponentMenuOpen && componentDetailOpen && (
          <ComponentPicker
            allowedTypes={allowedTypes}
            selectedType={selectedType}
            onSelectType={selectType}
            detailOpen={componentDetailOpen}
            onOpenDetail={() => setComponentDetailOpen(true)}
            onBack={() => {
              setComponentDetailOpen(false);
              setIsComponentFlyoutPinned(true);
              setShowComponentPickerView(true);
              setSearchInput('');
            }}
            searchInput={searchInput}
            onSearchChange={setSearchInput}
            onSearchCommit={commitSearch}
            items={activeCache.items}
            loading={activeCache.loading}
            loadingMore={activeCache.loadingMore}
            error={activeCache.error}
            onLoadMore={loadMore}
            onPickItem={applyAttachedComponent}
            menuRef={componentMenuRef}
          />
        )}

        {/* Quoted Message Preview */}
        {quotedMessage && onCancelQuote && activeAgent && (
          <QuotedMessagePreview
            quotedMessage={quotedMessage}
            onCancel={onCancelQuote}
            activeAgent={activeAgent}
          />
        )}

        {attachedComponent && (
          <div className="mb-2 inline-flex h-9 w-fit max-w-full min-w-0 items-center gap-2 self-start overflow-hidden rounded-[999px] bg-[#F2F2F4] p-2 opacity-100">
            <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center text-[#242429] [&>svg]:h-4 [&>svg]:w-4">
              <ComponentTypeIcon type={attachedComponent.type} />
            </span>
            <span className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap font-['Roboto',sans-serif] text-[14px] font-normal leading-[18px] text-[#242429] align-middle">
              {getComponentChipLabel(attachedComponent)}
            </span>
            <button
              type="button"
              className="inline-flex h-5 w-5 shrink-0 items-center justify-center gap-2.5 rounded-[21px] border-0 bg-white p-0 text-[#242429] hover:opacity-90 [&>svg]:h-3 [&>svg]:w-3"
              onClick={() => setAttachedComponent(null)}
              aria-label="Gỡ thành phần"
            >
              <CloseIcon />
            </button>
          </div>
        )}

        {(() => {
          const selectedFeature = selectedPlusFeatureKey
            ? plusMenuItems.find((item) => item.key === selectedPlusFeatureKey && item.selectable)
            : null;
          if (!selectedFeature) return null;
          return (
            <div className="mb-2 inline-flex h-9 w-fit max-w-full min-w-0 items-center gap-2 self-start overflow-hidden rounded-[999px] bg-[#F2F2F4] p-2 opacity-100">
              <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center text-[#242429] [&>svg]:h-5 [&>svg]:w-5 [&>svg]:text-[#242429]">
                {selectedFeature.icon}
              </span>
              <span className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap font-['Roboto',sans-serif] text-[14px] font-normal leading-[18px] text-[#242429] align-middle">
                {selectedFeature.label}
              </span>
              <button
                type="button"
                className="inline-flex h-5 w-5 shrink-0 items-center justify-center gap-2.5 rounded-[21px] border-0 bg-white p-0 text-[#242429] hover:opacity-90 [&>svg]:h-3 [&>svg]:w-3"
                onClick={() => setSelectedPlusFeatureKey(null)}
                aria-label="Gỡ tính năng"
              >
                <CloseIcon />
              </button>
            </div>
          );
        })()}

        {imageEntries.length > 0 && (
          <div className="image-preview-container">
            {imageEntries.map((entry, index) => (
              <div key={entry.id} className="image-preview-item">
                <img
                  src={entry.previewUrl}
                  alt={`Preview ${index + 1}`}
                  className="image-preview-thumbnail"
                />
                {entry.status === 'uploading' && (
                  <div className="image-preview-upload-overlay" aria-busy="true" aria-live="polite">
                    <ImageUploadingIcon className="image-preview-upload-spinner" />
                  </div>
                )}
                {entry.status === 'error' && (
                  <div
                    className="image-preview-upload-overlay image-preview-error-overlay"
                    role="button"
                    tabIndex={0}
                    onClick={() => handleRetryUploadImage(index)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') handleRetryUploadImage(index);
                    }}
                    aria-label="Thử lại gửi ảnh"
                  >
                    <ImageUploadErrorIcon className="image-preview-error-icon" />
                  </div>
                )}
                {/* Figma: ẩn nút xoá khi đang tải ảnh */}
                {entry.status !== 'uploading' && (
                  <button
                    type="button"
                    className="image-preview-remove"
                    onClick={() => handleRemoveImage(index)}
                    aria-label="Remove image"
                  >
                    <CloseIcon />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        <div className={`input-message-row mx-auto flex min-h-[44px] h-auto w-[332px] max-w-full items-center gap-2 ${isMultilineText ? 'has-text' : ''}`}>
          <div className="shrink-0">
            {renderLeftIcon()}
          </div>
          <textarea
            ref={textareaRef}
            id="comment"
            className={getTextareaClassName(Boolean(isVoiceActive || listening))}
            placeholder={displayPlaceholder}
            value={inputValue}
            maxLength={2000}
            onChange={(e) => handleInputChange(e.target.value)}
            onFocus={(e) => {
              if (listening) {
                  e.target.blur();
                  return;
              }
              setIsFocused(true);
            }}
            disabled={listening ? false : isChatInputDisabled}
            readOnly={listening}
            onBlur={() => {
              // Delay hide to allow click
              setTimeout(() => setIsFocused(false), 200);
            }}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            rows={1}
          />
          {listening ? (
            <div className="flex shrink-0 items-center gap-2">
              <button
                className="input-message-action-btn voice-pause-btn !w-[52px] !h-10 !rounded-[20px]"
                onClick={onPauseVoice}
                type="button"
                data-tooltip-id="chatbot-voice-tooltip"
                data-tooltip-content="Hoàn thành nhập bằng giọng nói"
                data-tooltip-place="top-end"
                aria-label="Hoàn thành nhập bằng giọng nói"
              >
                <PauseVoiceIcon />
              </button>
              {(inputValue.trim() || imageEntries.length > 0 || attachedComponent) && (
                <button
                  className="input-message-action-btn voice-send-btn !w-[52px] !h-10 !rounded-[20px]"
                  onClick={onSend}
                  onMouseUp={(e) => (e.target as HTMLButtonElement).blur()}
                  type="button"
                  data-tooltip-id="chatbot-tooltip"
                  data-tooltip-content="Gửi"
                  data-tooltip-place="top"
                  aria-label="Gửi"
                >
                  <SendNewIcon />
                </button>
              )}
            </div>
          ) : isVoiceActive ? (
            <div className="flex shrink-0 items-center gap-2">
              {(inputValue.trim() || imageEntries.length > 0 || attachedComponent) ? (
                <button
                  className="input-message-action-btn voice-send-btn !w-[52px] !h-10 !rounded-[20px]"
                  onClick={onSend}
                  onMouseUp={(e) => (e.target as HTMLButtonElement).blur()}
                  type="button"
                  data-tooltip-id="chatbot-tooltip"
                  data-tooltip-content="Gửi"
                  data-tooltip-place="top"
                  aria-label="Gửi"
                >
                  <SendNewIcon />
                </button>
              ) : (
                <MicButton
                  disabled={Boolean(isMicDisabled || isThinking || uploadLoading)}
                  onClick={onMicClickInternal}
                />
              )}
            </div>
          ) : isBotStreaming ? (
            <button
              className="input-message-action-btn send-btn flex !h-10 !w-[52px] shrink-0 items-center justify-center gap-2.5 !rounded-[20px] border-0 opacity-100"
              style={{ background: 'linear-gradient(180deg, #EE0033 0%, #FF8D58 100%)' }}
              onClick={onStopStreaming}
              onMouseUp={(e) => (e.target as HTMLButtonElement).blur()}
              type="button"
              data-tooltip-id="chatbot-tooltip"
              data-tooltip-content="Ngưng tạo câu trả lời"
              data-tooltip-place="top"
            >
              <PauseIcon />
            </button>
          ) : inputValue.trim() || imageEntries.length > 0 || attachedComponent ? (
            <button
              className="input-message-action-btn voice-send-btn !w-[52px] !h-10 !rounded-[20px] disabled:cursor-not-allowed disabled:opacity-50"
              onClick={onSend}
              onMouseUp={(e) => (e.target as HTMLButtonElement).blur()}
              type="button"
              disabled={isThinking || uploadLoading}
              data-tooltip-id="chatbot-tooltip"
              data-tooltip-content="Gửi"
              data-tooltip-place="top"
            >
              <SendNewIcon/>
            </button>
          ) : (
            <MicButton
              disabled={Boolean(isMicDisabled || isThinking || uploadLoading)}
              onClick={onMicClickInternal}
            />
          )}
        </div>
      </div>
      </div>
      <div className="input-message-disclaimer">
        {t('common.aiDisclaimer')}
      </div>
    </div>
  );
};

export default InputMessage;
